import { zodResolver } from '@hookform/resolvers/zod';
import { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AcceptInvitationHeader } from '@/features/partners/components/layouts/accept-partner-invitation-header';
import { useAcceptSubprojectInvitationStore } from '@/features/partners/store/use-accept-subproject-invitation-store';
import { FlowSubmitButton } from '@/shared/components/flow/flow-actions';
import { FlowFormFields } from '@/shared/components/flow/flow-fields';
import {
  Card,
  CardTitle,
  CardHeader,
  CardContent,
  CardFooter,
} from '@/shared/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import { LabelInput } from '@/shared/components/ui/label-input';
import { LabelTextarea } from '@/shared/components/ui/label-textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Org, Project } from '@/shared/types/api';

const acceptInvitationInfoFormSchema = (
  t: TFunction<'subproject', 'AcceptInvitation.InfoForm'>,
) =>
  z.object({
    orgId: z.string().min(1, t('form_field.org.required')),
    subprojectName: z.string().min(1, t('form_field.subproject_name.required')),
    subprojectDescription: z.string().optional(),
    contactPerson: z.string().min(1, t('form_field.contact_person.required')),
  });

export const AcceptInvitationInfoPage = ({
  clientSubproject,
  orgs,
}: {
  clientSubproject: Project;
  orgs: Org[];
}) => {
  const { t } = useTranslation('subproject', {
    keyPrefix: 'AcceptInvitation.InfoForm',
  });
  const { t: commonT } = useTranslation('common');

  const formSchema = acceptInvitationInfoFormSchema(t);

  const { data: acceptData, setData: setAcceptData } =
    useAcceptSubprojectInvitationStore();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      orgId: acceptData.orgId?.toString() || (orgs[0]?.id?.toString() ?? ''),
      subprojectName: acceptData.subprojectName || clientSubproject.name || '',
      subprojectDescription:
        acceptData.subprojectDescription || clientSubproject.description || '',
      contactPerson: acceptData.contactPerson || '',
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    setAcceptData({
      orgId: Number(values.orgId),
      subprojectName: values.subprojectName,
      subprojectDescription: values.subprojectDescription,
      contactPerson: values.contactPerson,
      step: 'category',
    });
  }

  return (
    <>
      <AcceptInvitationHeader />
      <Card variant="form" wrapWithStars>
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent>
              <FlowFormFields>
                <FormField
                  control={form.control}
                  name="orgId"
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger isInputTrigger>
                            <SelectValue
                              placeholder={t('org_placeholder')}
                              asLabelInput
                              label={t('org_label')}
                            >
                              {field.value
                                ? orgs.find(
                                    (org) => org.id.toString() === field.value,
                                  )?.name
                                : null}
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {orgs.map((org) => (
                            <SelectItem key={org.id} value={org.id.toString()}>
                              {org.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="subprojectName"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <LabelInput
                          label={t('subproject_name_label')}
                          placeholder={t('subproject_name_placeholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="subprojectDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <LabelTextarea
                          label={
                            t('subproject_description_label') +
                            ' ' +
                            commonT('hint.optional')
                          }
                          className="h-24"
                          placeholder={t('subproject_description_placeholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contactPerson"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <LabelInput
                          label={t('contact_person_label')}
                          placeholder={t('contact_person_placeholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FlowFormFields>
            </CardContent>
            <CardFooter>
              <FlowSubmitButton>{t('submit_button_label')}</FlowSubmitButton>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </>
  );
};
